import moment, { Moment } from 'moment';
import { dynamicString } from '@home/utils/dynamicString';
import { get } from 'lodash-es';
import type {
  IDateCallbackData,
  IDateFilterConfig,
  TDateInputValue,
  TDateSourceData,
  TPickerType,
  TRangeValue,
} from './type';

/**
 * 解析接口默认日期配置的根字段。
 * 用于判断外部数据是否已经准备完成，避免首屏过早回退到 dateSubtract 默认值。
 */
export const getApiDefaultRootKey = (dataPath?: string): string => {
  if (!dataPath) {
    return '';
  }

  const rawPath = dataPath.includes('{{')
    ? dataPath.replace(/\{\{(.*?)\}\}/, '$1').split('|')[0]?.trim()
    : dataPath;

  return rawPath?.split('.')[0] || '';
};

/**
 * 从外部数据中解析接口返回的默认日期值。
 * 支持普通点路径和 dynamicString 模板两种写法。
 */
export const resolveApiDefaultDate = (
  dataPath: string | undefined,
  data: TDateSourceData | undefined,
): string | null => {
  if (!dataPath || !data) {
    return null;
  }

  const resolvedValue = dataPath.includes('{{')
    ? dynamicString(dataPath, data, '').trim()
    : get(data, dataPath, '');

  return typeof resolvedValue === 'string' && resolvedValue ? resolvedValue : null;
};

/**
 * 判断接口默认日期依赖的外部数据是否已经 ready。
 * 这里不是判断日期值本身是否存在，而是判断对应的数据根节点是否已返回。
 */
export const checkApiDefaultDataReady = (
  data: TDateSourceData | undefined,
  apiDefaultRootKey: string,
  hasApiDefaultConfig: boolean,
): boolean => {
  if (!hasApiDefaultConfig) {
    return true;
  }

  if (!data) {
    return false;
  }

  if (!apiDefaultRootKey) {
    return true;
  }

  const rootValue = get(data, apiDefaultRootKey);
  return rootValue !== null && rootValue !== undefined;
};

/**
 * 生成单月筛选器的请求参数：
 * 起始为当年 1 月 1 日，结束为当前月最后一天。
 */
export const getMonthPayload = (val: Moment): IDateCallbackData => ({
  startDate: val.clone().startOf('year').format('YYYY-MM-DD'),
  endDate: val.clone().endOf('month').format('YYYY-MM-DD'),
  dateType: 'monthly',
});

/**
 * 生成自定义月份范围的请求参数。
 */
export const getMonthRangePayload = (val: TRangeValue): IDateCallbackData => ({
  startDate: val?.[0]?.clone().startOf('month').format('YYYY-MM-DD'),
  endDate: val?.[1]?.clone().endOf('month').format('YYYY-MM-DD'),
  dateType: 'monthly',
});

/**
 * 生成日筛选器的请求参数。
 */
export const getDayPayload = (val: string): IDateCallbackData => ({
  date: val,
  dateType: 'daily',
});

/**
 * 生成 Year To Date 日筛选器的请求参数：起始为当年 1 月 1 日，结束为选中日期。
 */
export const getDayAsYtdPayload = (val: string): IDateCallbackData => {
  const m = moment(val, 'YYYY-MM-DD');
  return {
    startDate: m.clone().startOf('year').format('YYYY-MM-DD'),
    endDate: val,
    dateType: 'daily',
  };
};

/**
 * 生成日区间筛选器的请求参数。
 */
export const getDayRangePayload = (val: TRangeValue): IDateCallbackData => ({
  startDate: val?.[0]?.format('YYYY-MM-DD'),
  endDate: val?.[1]?.format('YYYY-MM-DD'),
  dateType: 'daily',
});

/**
 * 计算日区间选择器的默认天数区间。
 */
export const getDefaultDayRange = (
  rangeStartSubtract: number,
  rangeEndSubtract: number,
  timeZone: number,
): [Moment, Moment] => {
  const base = moment().utcOffset(timeZone);
  const start = base.clone().subtract(rangeStartSubtract, 'days');
  const end = base.clone().subtract(rangeEndSubtract, 'days');
  return [start, end];
};

/**
 * 计算月范围选择器的默认月份区间。
 */
export const getDefaultMonthRange = (
  rangeStartSubtract: number,
  rangeEndSubtract: number,
  timeZone: number,
): [Moment, Moment] => {
  const base = moment().utcOffset(timeZone);
  const start = base.clone().subtract(rangeStartSubtract, 'months').startOf('month');
  const end = base.clone().subtract(rangeEndSubtract, 'months').endOf('month');
  return [start, end];
};

/**
 * 月选择器可选区间：
 * - 下界：disabledStart 所在月
 * - 上界：
 *   - disableCurrent=true：上一自然月（当月不可选）
 *   - disableCurrent=false：当月（disableFuture 单独生效时上界为当月，未来本就不可选）
 * 注：调用方应在 disableCurrent || disableFuture 时才调用此函数。
 */
export const getHistoricalMonthSelectableBounds = (
  disabledStart: string,
  timeZone: number,
  disableCurrent = false,
  _disableFuture = false,
): {
  lowerBound: Moment;
  upperBound: Moment;
} => {
  const now = moment().utcOffset(timeZone);
  const lowerBound = moment(disabledStart).startOf('month');
  const upperBound = disableCurrent
    ? now.clone().startOf('month').subtract(1, 'month')
    : now.clone().startOf('month');

  if (lowerBound.isAfter(upperBound, 'month')) {
    return { lowerBound: upperBound.clone(), upperBound: upperBound.clone() };
  }

  return { lowerBound, upperBound };
};

/**
 * day 选择器可选区间：
 * - 下界：disabledStart 日
 * - 上界：
 *   - disableCurrent=true：昨天（今天不可选）
 *   - disableCurrent=false：今天（disableFuture 单独生效时上界为今天，未来本就不可选）
 * 注：调用方应在 disableCurrent || disableFuture 时才调用此函数。
 */
export const getHistoricalDaySelectableBounds = (
  disabledStart: string,
  timeZone: number,
  disableCurrent = false,
  _disableFuture = false,
): {
  lowerBound: Moment;
  upperBound: Moment;
} => {
  const now = moment().utcOffset(timeZone);
  const lowerBound = moment(disabledStart).startOf('day');
  const upperBound = disableCurrent
    ? now.clone().startOf('day').subtract(1, 'day')
    : now.clone().startOf('day');

  if (lowerBound.isAfter(upperBound, 'day')) {
    return { lowerBound: upperBound.clone(), upperBound: upperBound.clone() };
  }

  return { lowerBound, upperBound };
};

/**
 * 单月选择：将目标月钳到可选区间内。
 */
export const clampMonthPickerValue = (
  target: Moment,
  timeZone: number,
  disabledStart: string,
  disableCurrent = false,
  disableFuture = false,
): Moment => {
  if (!disableCurrent && !disableFuture) {
    return target;
  }

  const { lowerBound, upperBound } = getHistoricalMonthSelectableBounds(
    disabledStart,
    timeZone,
    disableCurrent,
    disableFuture,
  );
  if (target.isBefore(lowerBound, 'month')) {
    return lowerBound.clone();
  }
  if (target.isAfter(upperBound, 'month')) {
    return upperBound.clone();
  }
  return target;
};

/**
 * 单日选择：将目标日钳到可选区间内。
 */
export const clampDayPickerValue = (
  target: Moment,
  timeZone: number,
  disabledStart: string,
  disableCurrent = false,
  disableFuture = false,
): Moment => {
  if (!disableCurrent && !disableFuture) {
    return target;
  }

  const { lowerBound, upperBound } = getHistoricalDaySelectableBounds(
    disabledStart,
    timeZone,
    disableCurrent,
    disableFuture,
  );
  if (target.isBefore(lowerBound, 'day')) {
    return lowerBound.clone();
  }
  if (target.isAfter(upperBound, 'day')) {
    return upperBound.clone();
  }
  return target;
};

export const initDefaultFilterValue = (
  pickerType: TPickerType,
  dateSubtract: number,
  timeZone: number,
  defaultMonthRange: [Moment, Moment],
  setTimeValue: (val: string) => void,
  setMonthValue: (val: string) => void,
  setMonthRange: (val: TRangeValue) => void,
  getData: (params: IDateCallbackData) => void,
  disabledStart = '2025-01-01',
  defaultDayRange?: [Moment, Moment],
  setDayRange?: (val: TRangeValue) => void,
  dayAsYtd = false,
  disableCurrent = false,
  disableFuture = false,
): void => {
  if (pickerType === 'month') {
    const base = moment().utcOffset(timeZone);
    let target = base.subtract(dateSubtract, 'months');
    target = clampMonthPickerValue(
      target,
      timeZone,
      disabledStart,
      disableCurrent,
      disableFuture,
    );
    const defaultVal = target.format('YYYY-MM');
    setMonthValue(defaultVal);
    getData(getMonthPayload(target));
    return;
  }

  if (pickerType === 'monthRange') {
    setMonthRange(defaultMonthRange);
    getData(getMonthRangePayload(defaultMonthRange));
    return;
  }

  if (pickerType === 'dayRange' && defaultDayRange && setDayRange) {
    setDayRange(defaultDayRange);
    getData(getDayRangePayload(defaultDayRange));
    return;
  }

  const base = moment().utcOffset(timeZone);
  let target = base.subtract(dateSubtract, 'days');
  target = clampDayPickerValue(target, timeZone, disabledStart, disableCurrent, disableFuture);
  const defaultVal = target.format('YYYY-MM-DD');
  setTimeValue(defaultVal);
  getData(dayAsYtd ? getDayAsYtdPayload(defaultVal) : getDayPayload(defaultVal));
};

/**
 * 将接口返回的默认日期应用到筛选器状态，并同步触发外部回调。
 * 返回值表示是否应用成功，便于上层决定是否继续走兜底初始化逻辑。
 */
export const applyApiDefaultFilterValue = (
  apiDateValue: string | null,
  pickerType: TPickerType,
  setTimeValue: (val: string) => void,
  setMonthValue: (val: string) => void,
  setMonthRange: (val: TRangeValue) => void,
  getData: (params: IDateCallbackData) => void,
  timeZone = 0,
  disabledStart = '2025-01-01',
  setDayRange?: (val: TRangeValue) => void,
  dayAsYtd = false,
  disableCurrent = false,
  disableFuture = false,
): boolean => {
  if (!apiDateValue) {
    return false;
  }

  const target = moment(apiDateValue, 'YYYYMM');
  if (!target.isValid()) {
    return false;
  }

  if (pickerType === 'month') {
    const clamped = clampMonthPickerValue(
      target,
      timeZone,
      disabledStart,
      disableCurrent,
      disableFuture,
    );
    setMonthValue(clamped.format('YYYY-MM'));
    getData(getMonthPayload(clamped));
    return true;
  }

  if (pickerType === 'monthRange') {
    const rangeValue: TRangeValue = [
      target.clone().startOf('month'),
      target.clone().endOf('month'),
    ];
    setMonthRange(rangeValue);
    getData(getMonthRangePayload(rangeValue));
    return true;
  }

  if (pickerType === 'dayRange' && setDayRange) {
    const rangeValue: TRangeValue = [
      target.clone(),
      target.clone(),
    ];
    setDayRange(rangeValue);
    getData(getDayRangePayload(rangeValue));
    return true;
  }

  const clamped = clampDayPickerValue(target, timeZone, disabledStart, disableCurrent, disableFuture);
  const dayValue = clamped.format('YYYY-MM-DD');
  setTimeValue(dayValue);
  getData(dayAsYtd ? getDayAsYtdPayload(dayValue) : getDayPayload(dayValue));
  return true;
};

/**
 * 规范化日期选择器 onChange 的输入值。
 * 由于 Tea 组件和封装组件对 onChange 的类型定义不完整，这里统一收敛成字符串。
 */
export const normalizeDayPickerValue = (val: TDateInputValue): string => {
  if (typeof val === 'string') {
    return val;
  }

  if (moment.isMoment(val)) {
    return val.format('YYYY-MM-DD');
  }

  return '';
};

/**
 * 返回月份范围配置中最终生效的开始/结束偏移值。
 */
export const getRangeSubtractConfig = (
  dateFilterConfig?: IDateFilterConfig,
): { rangeStartSubtract: number; rangeEndSubtract: number; } => {
  const dateSubtract = dateFilterConfig?.dateSubtract ?? 1;
  return {
    rangeStartSubtract: dateFilterConfig?.startSubtract ?? dateSubtract,
    rangeEndSubtract: dateFilterConfig?.endSubtract ?? dateSubtract,
  };
};
